/** @graphql cc **/

import React, { type FC, useContext, useState, useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type { GraphQLError } from 'graphql';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button/new';
import Select, { type StylesConfig } from '@atlaskit/select';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import CrossIcon from '@atlaskit/icon/core/cross';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { token } from '@atlaskit/tokens';
import { FadeIn } from '@atlaskit/motion';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { CONTENT_ACCESS_REQUEST_DECISION_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import type { useAccessRequestsQuery_confluence_contentAccessRequestByStatus_ConfluenceContentAccessRequestConnection_nodes_ConfluenceContentAccessRequest as ContentAccessRequestNode } from './__types__/useAccessRequestsQuery.ts';
import {
	type AccessRequestsTableRowMutation,
	type AccessRequestsTableRowMutation_confluence_updateContentAccessRequest_ConfluenceUpdateContentAccessRequestPayload_errors_MutationError,
	type AccessRequestsTableRowMutation_confluence_updateContentAccessRequest_ConfluenceUpdateContentAccessRequestPayload_errors_MutationError_extensions,
	type AccessRequestsTableRowMutation_confluence_updateContentAccessRequest_ConfluenceUpdateContentAccessRequestPayload_errors_MutationError_extensions_ConfluenceUpdateContentAccessRequestMutationErrorExtension,
	type AccessRequestsTableRowMutationVariables,
	type ConfluenceContentAccessRequestStatus,
	ConfluenceRequestAccessApprovalDecision,
	ResourceAccessType,
} from './__types__/AccessRequestsTableRowMutation.ts';

const i18n = defineMessages({
	mutationErrorGenericFlagTitle: {
		id: 'access-requests.mutation-error-generic-flag-title',
		defaultMessage: "Can't resolve the request",
		description:
			'The title text displayed when there is an error when approving or denying an access request',
	},
	mutationErrorGenericFlagDescription: {
		id: 'access-requests.mutation-error-generic-flag-description',
		defaultMessage: 'We ran into a problem resolving the access request. Try again later.',
		description:
			'The description text displayed when there is an error when approving or denying an access request',
	},
	mutationErrorAlreadyApprovedEditRequestFlagTitle: {
		id: 'access-requests.mutation-error-already-approved-edit-request-error-flag-title',
		defaultMessage: "{approverName} already approved {requesterName}'s request to edit",
		description: 'The title text displayed when an access request has already been approved',
	},
	mutationErrorAlreadyApprovedViewRequestFlagTitle: {
		id: 'access-requests.mutation-error-already-approved-view-request-error-flag-title',
		defaultMessage: "{approverName} already approved {requesterName}'s request to view",
		description: 'The title text displayed when an access request has already been approved',
	},
	mutationErrorAlreadyDeniedEditRequestFlagTitle: {
		id: 'access-requests.mutation-error-already-denied-edit-request-error-flag-title',
		defaultMessage: "{approverName} already denied {requesterName}'s request to edit",
		description: 'The title text displayed when an access request has already been denied',
	},
	mutationErrorAlreadyDeniedViewRequestFlagTitle: {
		id: 'access-requests.mutation-error-already-denied-view-request-error-flag-title',
		defaultMessage: "{approverName} already denied {requesterName}'s request to view",
		description: 'The title text displayed when an access request has already been denied',
	},
	accessRequestApproved: {
		id: 'access-requests.request-approved',
		defaultMessage: 'Approved',
		description: 'The text displayed when an access request has been approved',
	},
	accessRequestDenied: {
		id: 'access-requests.request-denied',
		defaultMessage: 'Denied',
		description: 'The text displayed when an access request has been denied',
	},
	approveButton: {
		id: 'access-requests.approve-button',
		defaultMessage: 'Approve',
		description: 'The text displayed on the button to approve an access request',
	},
	denyButton: {
		id: 'access-requests.deny-button',
		defaultMessage: 'Deny',
		description: 'The text displayed on the button to deny an access request',
	},
	accessTypeSelectorLabel_CanView: {
		id: 'access-requests.access-type-selector-label-can-view',
		defaultMessage: 'Can view',
		description: 'The label for the access type selector when the access type is "Can view"',
	},
	accessTypeSelectorLabel_CanEdit: {
		id: 'access-requests.access-type-selector-label-can-edit',
		defaultMessage: 'Can edit',
		description: 'The label for the access type selector when the access type is "Can edit"',
	},
});

export type MaybeStartBlockedAccessFlowForUser = (args: {
	id: string;
	avatarUrl: string;
	name: string;
	email: string;
	sitePermissionType: string;
	accessType: ResourceAccessType;
}) => Promise<{ isBlockedAccessFlowStarted: boolean }>;

interface AccessRequestsTableRowProps {
	contentId: string;
	accessRequest: ContentAccessRequestNode;
	isBlockedAccessFlowActive: boolean;
	maybeStartBlockedAccessFlowForUser: MaybeStartBlockedAccessFlowForUser;
	source: string;
	shouldBlockRowProcessing: boolean;
	onRowProcessingStart: () => void;
	onRowProcessingEnd: () => void;
	trackChange?: (
		requestId: string,
		newAccessType: ResourceAccessType,
		originalAccessType: ResourceAccessType,
	) => void;
	clearChanges?: () => void;
	/** Callback fired when an access request is successfully processed */
	onRequestProcessed?: (decision: 'approved' | 'denied') => void;
}

const rowStyles = xcss({
	margin: 'space.0',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	listStyle: 'none',
	minHeight: '52px',
	maxHeight: '52px',
});

const nameBoxStyles = xcss({
	marginLeft: 'space.150',
	marginRight: 'auto',
});

const buttonGroupStyles = xcss({
	marginLeft: 'space.150',
	flexShrink: 0,
});

const rowStatusStyles: React.CSSProperties = {
	flexShrink: 0,
	height: '40px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'start',
	paddingRight: token('space.150'),
};

interface AccessTypeOption {
	label: React.ReactNode;
	value: ResourceAccessType;
}

const ViewOption: AccessTypeOption = {
	label: <FormattedMessage {...i18n.accessTypeSelectorLabel_CanView} />,
	value: ResourceAccessType.VIEW,
};
const EditOption: AccessTypeOption = {
	label: <FormattedMessage {...i18n.accessTypeSelectorLabel_CanEdit} />,
	value: ResourceAccessType.EDIT,
};
const accessTypeOptions: AccessTypeOption[] = [ViewOption, EditOption];

const getSelectValueFromAccessType = (accessType: ResourceAccessType) =>
	accessTypeOptions.find(({ value }) => value === accessType) ?? ViewOption;

export const UPDATE_CONTENT_ACCESS_REQUEST_MUTATION = gql`
	mutation AccessRequestsTableRowMutation(
		$cloudId: ID!
		$updateContentAccessRequestInput: ConfluenceUpdateContentAccessRequestInput!
	) {
		confluence_updateContentAccessRequest(
			cloudId: $cloudId
			updateContentAccessRequestInput: $updateContentAccessRequestInput
		) {
			success
			errors {
				extensions {
					errorType
					statusCode
					... on ConfluenceUpdateContentAccessRequestMutationErrorExtension {
						displayName
						processedRequestStatus
					}
				}
				message
			}
		}
	}
`;

const SELECT_STYLES: { styles: StylesConfig<AccessTypeOption> } = {
	styles: {
		singleValue: (base) => ({ ...base, minWidth: '62px', textAlign: 'left' }),
	},
};

export const REQUEST_ALREADY_PROCESSED = 'REQUEST_ALREADY_PROCESSED';

const isAlreadyProcessedExtension = (
	extension: AccessRequestsTableRowMutation_confluence_updateContentAccessRequest_ConfluenceUpdateContentAccessRequestPayload_errors_MutationError_extensions | null,
): extension is AccessRequestsTableRowMutation_confluence_updateContentAccessRequest_ConfluenceUpdateContentAccessRequestPayload_errors_MutationError_extensions_ConfluenceUpdateContentAccessRequestMutationErrorExtension => {
	return (
		(
			extension as AccessRequestsTableRowMutation_confluence_updateContentAccessRequest_ConfluenceUpdateContentAccessRequestPayload_errors_MutationError_extensions_ConfluenceUpdateContentAccessRequestMutationErrorExtension
		)?.displayName !== undefined
	);
};

const getAlreadyProcessedErrorMessage = (
	status: ConfluenceContentAccessRequestStatus,
	requestType: ResourceAccessType,
) => {
	if (status === 'APPROVE') {
		return requestType === ResourceAccessType.VIEW
			? i18n.mutationErrorAlreadyApprovedViewRequestFlagTitle
			: i18n.mutationErrorAlreadyApprovedEditRequestFlagTitle;
	}

	return requestType === ResourceAccessType.VIEW
		? i18n.mutationErrorAlreadyDeniedViewRequestFlagTitle
		: i18n.mutationErrorAlreadyDeniedEditRequestFlagTitle;
};

export const AccessRequestsTableRowInner: FC<AccessRequestsTableRowProps & WithFlagsProps> = ({
	accessRequest,
	contentId,
	flags,
	isBlockedAccessFlowActive,
	maybeStartBlockedAccessFlowForUser,
	source,
	shouldBlockRowProcessing,
	onRowProcessingStart,
	onRowProcessingEnd,
	trackChange,
	clearChanges,
	onRequestProcessed,
}) => {
	const { formatMessage } = useIntl();
	const { cloudId } = useSessionData();
	const [accessTypeOption, setAccessTypeOption] = useState(
		getSelectValueFromAccessType(accessRequest.requestAccessType),
	);
	const accessType = accessTypeOption.value;
	const accountId = accessRequest.accessRequestedAaid;
	const requestStatus = accessRequest.status;
	const requestId = accessRequest.id;

	const displayName = accessRequest.user?.displayName ?? '';
	const email = accessRequest.user?.emails?.find((e) => e?.isPrimary)?.value ?? '';
	const avatarUrl = accessRequest.user?.photos?.find((photo) => photo?.isPrimary)?.value ?? '';

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleMutationError = (
		mutationErrors: AccessRequestsTableRowMutation_confluence_updateContentAccessRequest_ConfluenceUpdateContentAccessRequestPayload_errors_MutationError[],
		_: GraphQLError[],
	) => {
		const alreadyProcessedError = mutationErrors.find((e) =>
			e.extensions?.some((ext) => ext?.errorType === REQUEST_ALREADY_PROCESSED),
		);
		const alreadyProcessedExtension = alreadyProcessedError?.extensions?.find(
			isAlreadyProcessedExtension,
		);

		if (
			alreadyProcessedExtension?.displayName &&
			alreadyProcessedExtension.processedRequestStatus
		) {
			const approverName = alreadyProcessedExtension?.displayName;
			const requesterName = accessRequest.user?.displayName;
			const approveOrDenyTemplate = getAlreadyProcessedErrorMessage(
				alreadyProcessedExtension?.processedRequestStatus,
				accessType,
			);

			void flags.showErrorFlag({
				title: formatMessage(approveOrDenyTemplate, {
					approverName,
					requesterName,
				}),
			});
		} else {
			void flags.showErrorFlag({
				title: formatMessage(i18n.mutationErrorGenericFlagTitle),
				description: formatMessage(i18n.mutationErrorGenericFlagDescription),
			});
		}

		const message = mutationErrors[0]?.message || 'unknown';
		const errorType = mutationErrors[0]?.extensions?.[0]?.errorType || 'unknown';
		const statusCode = mutationErrors[0]?.extensions?.[0]?.statusCode || 'unknown';

		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'accessRequestsTableRowMutation',
				source,
				attributes: {
					requestId,
					contentId,
					accessType,
					accountId,
					message,
					errorType,
					statusCode,
				},
			},
		}).fire();
	};

	const [updateContentAccessRequestMutation] = useMutation<
		AccessRequestsTableRowMutation,
		AccessRequestsTableRowMutationVariables
	>(UPDATE_CONTENT_ACCESS_REQUEST_MUTATION);

	const [rowEntryAnimationFinished, setRowEntryAnimationFinished] = useState(false);
	const [rowStatusAnimationFinished, setRowStatusAnimationFinished] = useState(false);

	const [isBlockedAccessFlowStarted, setIsBlockedAccessFlowStarted] = useState(false);
	const [isRowDecisionBeingProcessed, setIsRowDecisionBeingProcessed] =
		useState<ConfluenceRequestAccessApprovalDecision | null>(null);

	/**
	 * Stops the "processing" look of the row only after the blocked access flow is completed
	 * This only happens if blocked access was needed in the first place
	 * */
	useEffect(() => {
		if (isRowDecisionBeingProcessed && isBlockedAccessFlowStarted && !isBlockedAccessFlowActive) {
			setIsRowDecisionBeingProcessed(null);
			setIsBlockedAccessFlowStarted(false);
			onRowProcessingEnd();
		}
	}, [
		isBlockedAccessFlowStarted,
		isBlockedAccessFlowActive,
		isRowDecisionBeingProcessed,
		onRowProcessingEnd,
	]);

	const createCtaHandler =
		(approvalDecision: ConfluenceRequestAccessApprovalDecision) => async () => {
			if (shouldBlockRowProcessing) {
				return;
			}

			experienceTracker.start({
				name: CONTENT_ACCESS_REQUEST_DECISION_EXPERIENCE,
				attributes: {
					decision: approvalDecision,
				},
			});
			onRowProcessingStart();
			setIsRowDecisionBeingProcessed(approvalDecision);

			const result = await updateContentAccessRequestMutation({
				variables: {
					cloudId,
					updateContentAccessRequestInput: {
						requestId,
						contentId,
						accessRequestedAccountId: accountId,
						approvalDecision,
						accessType,
					},
				},
				update(cache, incoming) {
					if (incoming?.data?.confluence_updateContentAccessRequest?.success) {
						cache.writeFragment({
							id: `ConfluenceContentAccessRequest:${accessRequest.id}`,
							fragment: gql`
								fragment UpdateConfluenceContentAccessRequest on ConfluenceContentAccessRequest {
									status
								}
							`,
							data: {
								status: approvalDecision,
								__typename: 'ConfluenceContentAccessRequest',
							},
						});
					}
				},
			});

			let isRowProcessingDone = true;
			const successfulMutation = result.data?.confluence_updateContentAccessRequest?.success;
			const successfulApproval = successfulMutation && approvalDecision === 'APPROVE';
			const successfulDenial = successfulMutation && approvalDecision === 'DENY';
			const processingTime = Date.now() - (accessRequest.creationDateTimestamp ?? 0);
			const creationDateTimestamp = accessRequest.creationDateTimestamp ?? 0;

			if (successfulApproval) {
				const { isBlockedAccessFlowStarted: isFlowStarted } =
					await maybeStartBlockedAccessFlowForUser({
						id: accountId,
						avatarUrl,
						name: displayName,
						email,
						sitePermissionType: accessType,
						accessType,
					});

				setIsBlockedAccessFlowStarted(isFlowStarted);
				isRowProcessingDone = !isFlowStarted;

				// Notify parent that an approval occurred (for triggering data refresh and count update)
				onRequestProcessed?.('approved');

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'granted',
						actionSubject: 'access',
						actionSubjectId: 'grantAccess',
						source,
						attributes: {
							accessType: accessType === ResourceAccessType.EDIT ? 'edit' : 'view',
							processingTime,
							creationDateTimestamp,
						},
					},
				}).fire();
			}

			if (successfulDenial) {
				// Notify parent that a denial occurred (for count update)
				onRequestProcessed?.('denied');

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'denyAccess',
						source,
						attributes: {
							accessType,
							processingTime,
							creationDateTimestamp,
						},
					},
				}).fire();
			}

			if (isRowProcessingDone) {
				setIsRowDecisionBeingProcessed(null);
				onRowProcessingEnd();
			}

			if (successfulMutation) {
				// Clear the tracked changes for this request since the mutation was successful
				if (clearChanges) {
					clearChanges();
				}

				experienceTracker.succeed({ name: CONTENT_ACCESS_REQUEST_DECISION_EXPERIENCE });
			} else {
				const mutationErrors = result.data?.confluence_updateContentAccessRequest?.errors ?? [];
				const apolloErrors = result.errors ?? [];
				handleMutationError(mutationErrors, apolloErrors);

				experienceTracker.fail({
					name: CONTENT_ACCESS_REQUEST_DECISION_EXPERIENCE,
					error: new Error('Update content access request error'),
				});
			}
		};

	const handleSelectChange = (newlySelectedOption: AccessTypeOption | null) => {
		if (newlySelectedOption !== null) {
			setAccessTypeOption(newlySelectedOption);
			// Track the change for unsaved changes detection
			if (trackChange) {
				trackChange(accessRequest.id, newlySelectedOption.value, accessRequest.requestAccessType);
			}
		}
	};

	const pendingRequestUI = (
		<Inline space="space.150" alignBlock="center" xcss={buttonGroupStyles}>
			<Select
				appearance="subtle"
				value={accessTypeOption}
				options={accessTypeOptions}
				isSearchable={false}
				menuPosition="fixed"
				onChange={handleSelectChange}
				isDisabled={isRowDecisionBeingProcessed !== null}
				menuShouldBlockScroll
				{...SELECT_STYLES}
			/>
			<Button
				appearance="default"
				onClick={createCtaHandler(ConfluenceRequestAccessApprovalDecision.DENY)}
				isLoading={isRowDecisionBeingProcessed === 'DENY'}
				isDisabled={isRowDecisionBeingProcessed === 'APPROVE'}
			>
				{formatMessage(i18n.denyButton)}
			</Button>
			<Button
				appearance="primary"
				onClick={createCtaHandler(ConfluenceRequestAccessApprovalDecision.APPROVE)}
				isLoading={isRowDecisionBeingProcessed === 'APPROVE'}
				isDisabled={isRowDecisionBeingProcessed === 'DENY'}
			>
				{formatMessage(i18n.approveButton)}
			</Button>
		</Inline>
	);

	const requestApproved = (
		<>
			<CheckMarkIcon label="" color={token('color.icon.success')} />
			<Text color="color.text.subtle">{formatMessage(i18n.accessRequestApproved)}</Text>
		</>
	);

	const requestDenied = (
		<>
			<CrossIcon label="" color={token('color.icon.danger')} />
			<Text color="color.text.subtle">{formatMessage(i18n.accessRequestDenied)}</Text>
		</>
	);

	const actionedRequestUI = (
		<FadeIn
			duration="large"
			entranceDirection="right"
			isPaused={rowStatusAnimationFinished}
			onFinish={() => setRowStatusAnimationFinished(true)}
			key={`${accessRequest.id}-request-status`}
		>
			{({ style, ...rest }) => (
				<div
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						...style,
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						...rowStatusStyles,
					}}
					{...rest}
				>
					<Inline alignBlock="center" space="space.150" xcss={buttonGroupStyles}>
						<Inline alignBlock="center" space="space.050">
							{requestStatus === 'APPROVE' ? requestApproved : requestDenied}
						</Inline>
					</Inline>
				</div>
			)}
		</FadeIn>
	);

	return (
		<FadeIn
			duration="large"
			isPaused={rowEntryAnimationFinished}
			onFinish={() => setRowEntryAnimationFinished(true)}
		>
			{({ ...props }) => (
				<div {...props}>
					<Inline alignInline="start" alignBlock="center" xcss={rowStyles} as="li">
						<Avatar src={avatarUrl} />
						<Stack xcss={nameBoxStyles} grow="fill">
							<Text color="color.text" maxLines={1}>
								{displayName}
							</Text>
							<Text color="color.text.subtlest" size="small" maxLines={1}>
								{email}
							</Text>
						</Stack>

						{(requestStatus === 'PENDING' || isRowDecisionBeingProcessed) && pendingRequestUI}
						{requestStatus !== 'PENDING' && !isRowDecisionBeingProcessed && actionedRequestUI}
					</Inline>
				</div>
			)}
		</FadeIn>
	);
};

export const AccessRequestsTableRow = withFlags(AccessRequestsTableRowInner);
